import { create } from 'zustand'
import { SavedOrder } from './state/orderState'
import { devtools } from 'utils/devtools'
import { immer } from 'zustand/middleware/immer'
import nanoid from '@ui/utils/nanoid'
import { useMemo } from 'react'
import { DiscountOption } from '@order/orderTypes'
import { ErpOrderDiscountedPrice } from '@ca/common'

type CartState = {
  orders: SavedOrder[]
}

type CartActions = {
  addOrder: (order: Omit<SavedOrder, 'id'>) => string
  removeOrder: (id: string) => void
  removeOrders: (ids: string[]) => void
  reset: () => void
  applyDiscountedOrderPrices: (
    orderId: string,
    orders: ErpOrderDiscountedPrice[]
  ) => void
  clearFinalPrices: (orderId: string, orderIds: number[]) => void
  discounts: {
    add: (orderId: string, discount: DiscountOption) => void
    remove: (orderId: string, discountId: string) => void
    update: (orderId: string, discount: DiscountOption) => void
  }
}

const useCartState = create<CartState & CartActions>()(
  devtools(
    immer((set, get, store) => ({
      orders: [],
      addOrder: (order) => {
        const id = nanoid()
        set((state) => {
          state.orders.push({
            ...order,
            id,
          })
        })
        return id
      },
      removeOrder: (id) => {
        set((state) => {
          state.orders = state.orders.filter((order) => order.id !== id)
        })
      },
      removeOrders: (ids) => {
        set((state) => {
          state.orders = state.orders.filter((order) => !ids.includes(order.id))
        })
      },
      reset: () => {
        set((state) => {
          state.orders = []
        })
      },

      applyDiscountedOrderPrices: (orderId, orders) => {
        set((state) => {
          const order = state.orders.find((o) => o.id === orderId)
          if (!order || !order.result || !orders.length) {
            return
          }
          const byId = new Map(orders.map((o) => [o.id, o.amount]))
          order.result.orders.forEach((item) => {
            const finalPrice = byId.get(item.id)
            if (finalPrice === undefined) {
              return
            }
            item.final_price = finalPrice
          })
        })
      },

      clearFinalPrices: (orderId, orderIds) => {
        set((state) => {
          const order = state.orders.find((o) => o.id === orderId)
          if (!order || !order.result || !orderIds.length) {
            return
          }
          const ids = new Set(orderIds)
          order.result.orders.forEach((item) => {
            if (!ids.has(item.id)) {
              return
            }
            item.final_price = undefined
          })
        })
      },

      discounts: {
        add: (orderID, discount) => {
          set((state) => {
            const order = state.orders.find((o) => o.id === orderID)
            if (!order) {
              return
            }
            order.discounts.push(discount)
          })
        },
        remove: (orderID, discountId) => {
          set((state) => {
            const order = state.orders.find((o) => o.id === orderID)
            if (!order) {
              return
            }
            order.discounts = order.discounts.filter((d) => d.id !== discountId)
          })
        },
        update: (orderID, discount) => {
          set((state) => {
            const order = state.orders.find((o) => o.id === orderID)
            if (!order) {
              return
            }
            order.discounts = order.discounts.map((d) =>
              d.id === discount.id ? discount : d
            )
          })
        },
      },
    })),
    {
      name: 'cart',
    }
  )
)

export const useSavedOrder = (id: string | undefined) => {
  const orders = useCartState((s) => s.orders)
  return useMemo(() => orders.find((o) => o.id === id), [orders, id])
}

export default useCartState
